export interface InputLine {
    source: string;
    reading: string;
    translation: string;
    directive: { [key: string]: unknown; name: string };
}

/** Parse one line of the existing $JSON$[label]source{{reading}}[[gloss]] format. */
export const parseInputLine = (input: string): InputLine => {
    let source = input.trim();
    let settings: { [key: string]: unknown } = {};
    if (source.startsWith('$')) {
        const end = source.indexOf('$', 1);
        if (end < 0) throw new Error('Unclosed $JSON$ directive.');
        try {
            const value = JSON.parse(source.slice(1, end));
            if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error();
            settings = value;
        } catch (_) {
            throw new Error('The $JSON$ directive must contain a JSON object.');
        }
        source = source.slice(end + 1).trimStart();
    }
    let name = '';
    if (source.startsWith('[') && !source.startsWith('[[')) {
        const end = source.indexOf(']');
        if (end < 0) throw new Error('Unclosed panel label.');
        name = source.slice(1, end);
        source = source.slice(end + 1).trimStart();
    }
    // Structured word records may contain their own punctuation/markup.
    if (source.startsWith('<<')) {
        if (!source.endsWith('>>')) throw new Error('Unclosed word record.');
        return { source, reading: '', translation: '', directive: { ...settings, name } };
    }
    const match = /^(.*?)(?:\{\{([^{}]*)\}\})?(?:\[\[([^\]]*)\]\])?$/.exec(source);
    if (!match || /\{\{|\}\}|\[\[|\]\]/.test(match[1])) {
        throw new Error('Unclosed or misplaced reading/translation annotation.');
    }
    return {
        source: match[1], reading: match[2] || '', translation: match[3] || '',
        directive: { ...settings, name },
    };
};

export interface CustomWord { char: string; jp: string; trans: string; }

export const parseCustomWords = (input: string): CustomWord[] => input.split(/\r?\n/)
    .filter((line) => line.trim()).map((line, index) => {
        const entry = parseInputLine(line);
        if (!entry.source || !entry.reading || !entry.translation) {
            throw new Error(`Custom word ${index + 1} needs source{{jyutping}}[[translation]].`);
        }
        return { char: entry.source, jp: entry.reading, trans: entry.translation };
    });
